import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { BRAND_LOGO, HEADER_UTILITY_NAV, NAV_SECTIONS } from "../../data/platformPresentation";

function isJoinPath(pathname) {
  return (
    pathname === "/join" ||
    pathname === "/join-our-portfolio" ||
    pathname === "/signup" ||
    pathname === "/register"
  );
}

function utilityNavClassName(item) {
  const active = Boolean(item.utilityActive);
  return `nh-rh__utility-link${active ? " nh-rh__utility-link--active" : ""}`;
}

function scrollToSection(sectionId) {
  const el = document.getElementById(sectionId);
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  window.history.replaceState(null, "", `#${sectionId}`);
  return true;
}

function SectionNavControl({ item, className, as = "link", onNavigated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const onActivate = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      if (!scrollToSection(item.section)) {
        navigate(`/#${item.section}`);
      }
    } else {
      navigate(`/#${item.section}`);
    }
    onNavigated?.();
  };

  if (as === "button") {
    return (
      <button type="button" className={className} onClick={onActivate}>
        {item.label}
      </button>
    );
  }

  return (
    <a href={`/#${item.section}`} className={className} onClick={onActivate}>
      {item.label}
    </a>
  );
}

export default function MarketingSiteHeader() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const headerClass = [
    "nh-rh__header",
    "nh-rh__header--capital",
    scrolled ? "nh-rh__header--scrolled" : "",
    menuOpen ? "nh-rh__header--menu-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const spacerClass = `nh-rh__header-spacer${scrolled ? " nh-rh__header-spacer--scrolled" : ""}`;

  const renderNavItems = (onNavigated) =>
    NAV_SECTIONS.map((item) => {
      const active =
        (item.to === "/signup" || item.to === "/join") && isJoinPath(pathname);
      return (
        <li key={item.label} className={`nh-rh__nav-item${active ? " nh-rh__nav-item--active" : ""}`}>
          {item.section ? (
            <SectionNavControl
              item={item}
              className="nh-rh__nav-link--capital"
              onNavigated={onNavigated}
            />
          ) : (
            <Link to={item.to} className="nh-rh__nav-link--capital" onClick={onNavigated}>
              {item.label}
            </Link>
          )}
        </li>
      );
    });

  return (
    <>
      <header className={headerClass}>
        <div className="nh-rh__utility-bar">
          <nav className="nh-rh__utility-nav" aria-label="Need Healthcare sites">
            {HEADER_UTILITY_NAV.map((item) => {
              if (item.section) {
                return (
                  <SectionNavControl
                    key={item.label}
                    item={item}
                    className={utilityNavClassName(item)}
                  />
                );
              }
              if (item.external) {
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    className={utilityNavClassName(item)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <Link key={item.label} to={item.to} className={utilityNavClassName(item)}>
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="nh-rh__header-main">
          <div className="nh-rh__header-bar">
            <Link to="/" className="nh-rh__logo-mark" aria-label={BRAND_LOGO.ariaLabel} onClick={closeMenu}>
              <span className="nh-rh__logo-need-health">{BRAND_LOGO.needHealth}</span>
              <span className="nh-rh__logo-care">{BRAND_LOGO.care}</span>
            </Link>
            <button
              type="button"
              className="nh-rh__nav-toggle"
              aria-expanded={menuOpen}
              aria-controls="nh-rh-primary-nav-mobile"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="nh-rh__nav-toggle-bar" aria-hidden />
              <span className="nh-rh__nav-toggle-bar" aria-hidden />
              <span className="nh-rh__nav-toggle-bar" aria-hidden />
            </button>
            <nav className="nh-rh__nav nh-rh__nav--capital nh-rh__nav--desktop" aria-label="Primary">
              <ul className="nh-rh__nav-list">{renderNavItems()}</ul>
            </nav>
          </div>
        </div>

        <div
          id="nh-rh-primary-nav-mobile"
          className={`nh-rh__nav-drawer${menuOpen ? " nh-rh__nav-drawer--open" : ""}`}
          hidden={!menuOpen}
        >
          <nav aria-label="Primary mobile">
            <ul className="nh-rh__nav-drawer-list">{renderNavItems(closeMenu)}</ul>
          </nav>
        </div>
      </header>
      {menuOpen ? (
        <button type="button" className="nh-rh__nav-backdrop" aria-label="Close menu" onClick={closeMenu} />
      ) : null}
      <div className={spacerClass} aria-hidden />
    </>
  );
}
